import { Delta } from "@vueup/vue-quill";

const PARAGRAPH_TAGS = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6"]);
const BLOCK_ATTRIBUTE_KEYS = new Set(["align", "lineheight", "list", "indent", "hangingIndent", "leftIndent", "firstline"]);

const lineHeightToAttribute = (lineHeight?: string) => {
  if (!lineHeight) return undefined;

  if (lineHeight.includes("px") || lineHeight.includes("normal")) {
    switch (lineHeight) {
      case "normal":
        return "1";
      case "44px":
        return "3";
      case "36.6667px":
        return "2.5";
      case "29.3333px":
      case "32px":
        return "2";
      case "22px":
        return "1.5";
      case "16.8667px":
        return "1.15";
      default:
        return undefined;
    }
  }

  const parsedLineHeight = parseFloat(lineHeight);
  if (!Number.isFinite(parsedLineHeight)) return undefined;

  const value = parseFloat(parsedLineHeight.toFixed(2));
  const targets = [1, 1.15, 1.5, 2];
  return targets
    .reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    )
    .toString();
};

const blockAttributesFromElement = (element: Element) => {
  const styleObject = useConvertElementStyles(element);
  const attributes: Record<string, string> = {};
  const lineheight = lineHeightToAttribute(styleObject["line-height"]);

  if (lineheight) {
    attributes.lineheight = lineheight;
  }

  if (styleObject["text-align"]) {
    attributes.align = styleObject["text-align"];
  }

  if ((element as HTMLElement).style.marginLeft) {
    attributes.leftIndent = (element as HTMLElement).style.marginLeft;
  }
  if ((element as HTMLElement).style.textIndent) {
    attributes.firstline = (element as HTMLElement).style.textIndent;
  }
  if (element.classList.contains("hanging-indent-1")) {
    attributes.hangingIndent = "1";
  }

  return attributes;
};

const splitAttributes = (attributes?: Record<string, any>) => {
  const inlineAttributes: Record<string, any> = {};
  const blockAttributes: Record<string, any> = {};

  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (BLOCK_ATTRIBUTE_KEYS.has(key)) {
      blockAttributes[key] = value;
    } else {
      inlineAttributes[key] = value;
    }
  }

  return {
    inlineAttributes:
      Object.keys(inlineAttributes).length > 0 ? inlineAttributes : undefined,
    blockAttributes:
      Object.keys(blockAttributes).length > 0 ? blockAttributes : undefined,
  };
};

const appendOp = (delta: Delta, insert: any, attributes?: Record<string, any>) => {
  if (attributes && Object.keys(attributes).length > 0) {
    delta.insert(insert, attributes);
    return;
  }

  delta.insert(insert);
};

const normalizeBlockNewlines = (
  delta: Delta,
  fallbackBlockAttributes: Record<string, any> = {}
) => {
  const normalizedDelta = new Delta();

  for (const op of delta.ops ?? []) {
    if (typeof op.insert !== "string") {
      appendOp(normalizedDelta, op.insert, op.attributes);
      continue;
    }

    const parts = op.insert.split("\n");
    const { inlineAttributes, blockAttributes } = splitAttributes(op.attributes);

    for (let index = 0; index < parts.length; index++) {
      if (parts[index]) {
        appendOp(normalizedDelta, parts[index], inlineAttributes);
      }

      if (index < parts.length - 1) {
        appendOp(normalizedDelta, "\n", {
          ...fallbackBlockAttributes,
          ...blockAttributes,
        });
      }
    }
  }

  return normalizedDelta;
};

const endsWithNewline = (delta: Delta) => {
  const lastOp = delta.ops?.[delta.ops.length - 1];
  return typeof lastOp?.insert === "string" && lastOp.insert.endsWith("\n");
};

const appendDelta = (target: Delta, source: Delta) => {
  for (const op of source.ops ?? []) {
    appendOp(target, op.insert, op.attributes);
  }
};

export const useConvertImportedHtmlToQuillDelta = (quill: any, html: string) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = document.body.firstElementChild;

  if (!container || container.children.length === 0) {
    return quill.clipboard.convert(html);
  }

  const combinedDelta = new Delta();

  for (const child of Array.from(container.children)) {
    const convertedDelta = quill.clipboard.convert(child.outerHTML);
    const isParagraph = PARAGRAPH_TAGS.has(child.tagName);
    const blockAttributes = isParagraph ? blockAttributesFromElement(child) : {};
    const normalizedDelta = normalizeBlockNewlines(
      convertedDelta,
      blockAttributes
    );

    appendDelta(combinedDelta, normalizedDelta);

    if (isParagraph && !endsWithNewline(normalizedDelta)) {
      appendOp(combinedDelta, "\n", blockAttributes);
    }
  }

  return combinedDelta;
};
